import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "../App";

const DEFECT_TYPES = ["Dimensional", "Material", "Visual", "Surface Finish", "Process", "Other"];
const REASON_6M = ["Man", "Machine", "Material", "Method", "Measurement", "Mother Nature"];

export function DefectTable({ type, items, setItems }) {
  const isReject = type === "rejection";
  const color = isReject
    ? { bg: "bg-[#FEF2F2]", border: "border-[#FCA5A5]", header: "bg-[#EF4444]", text: "text-[#EF4444]", addBg: "bg-[#EF4444] hover:bg-[#DC2626]" }
    : { bg: "bg-[#FFFBEB]", border: "border-[#FDE68A]", header: "bg-[#F59E0B]", text: "text-[#F59E0B]", addBg: "bg-[#F59E0B] hover:bg-[#D97706]" };
  const label = isReject ? "Rejection" : "Rework";

  const [defectMaster, setDefectMaster] = useState([]);

  useEffect(() => {
    axios.get(`${API}/defect-master`).then(r => setDefectMaster(r.data)).catch(() => {});
  }, []);

  const totalQty = (items || []).reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);

  const addRow = () => {
    setItems([...(items || []), { qty: "", defect_type: "", defect_name: "", reason: "", action: "" }]);
  };

  const removeRow = (idx) => {
    const copy = [...items];
    copy.splice(idx, 1);
    setItems(copy);
  };

  const updateRow = (idx, field, value) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [field]: value };
    setItems(copy);
  };

  const defectOptions = defectMaster.length > 0
    ? defectMaster.map(dm => ({ value: dm.code, label: `${dm.code} — ${dm.name}`, name: dm.name }))
    : DEFECT_TYPES.map(t => ({ value: t, label: t, name: t }));

  return (
    <div className={`border ${color.border} rounded-lg p-4 ${color.bg}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`text-sm font-bold ${color.text}`}>
          {label} Details {items.length > 0 && `(${items.length} rows — Total: ${totalQty.toFixed(2)} Nos)`}
        </h4>
        <Button type="button" size="sm" onClick={addRow} className={`${color.addBg} text-white h-8 px-3 text-xs`} data-testid={`${type}-add-row-btn`}>
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[#64748B] text-center py-3">No {label.toLowerCase()} entries. Click "+ Add" to add rows.</p>
      ) : (
        <table className="w-full text-sm" data-testid={`${type}-details-table`}>
          <thead>
            <tr className={`${color.header} text-white`}>
              <th className="px-2 py-2 w-10 text-center">Sr</th>
              <th className="px-2 py-2 text-center w-[90px]">Quantity *</th>
              <th className="px-2 py-2 text-left">Defect *</th>
              <th className="px-2 py-2 text-left">Issue</th>
              <th className="px-2 py-2 text-left">Reason (6M) *</th>
              <th className="px-2 py-2 text-left">Action</th>
              <th className="px-2 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((d, i) => (
              <tr key={i} className={`border-b ${color.border}`}>
                <td className="px-2 py-2 text-center font-mono font-bold">{i + 1}</td>
                <td className="px-2 py-2">
                  <Input type="number" step="0.01" min="0" value={d.qty || ""}
                    onChange={(e) => updateRow(i, "qty", e.target.value)}
                    placeholder="0" className="min-h-[34px] text-sm bg-white font-mono text-center w-full" data-testid={`${type}-qty-${i}`} />
                </td>
                <td className="px-2 py-2">
                  <Select value={d.defect_type} onValueChange={(v) => {
                    const sel = defectOptions.find(o => o.value === v);
                    const copy = [...items];
                    copy[i] = { ...copy[i], defect_type: v, defect_name: sel?.name || v };
                    setItems(copy);
                  }}>
                    <SelectTrigger className="min-h-[34px] text-xs bg-white" data-testid={`${type}-defect-${i}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {defectOptions.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-2">
                  <Input value={d.defect_name || ""} readOnly tabIndex={-1}
                    className="min-h-[34px] text-xs bg-gray-50 cursor-default" data-testid={`${type}-issue-${i}`} />
                </td>
                <td className="px-2 py-2">
                  <Select value={d.reason} onValueChange={(v) => updateRow(i, "reason", v)}>
                    <SelectTrigger className="min-h-[34px] text-xs bg-white" data-testid={`${type}-reason-${i}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>{REASON_6M.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-2">
                  <Input value={d.action || ""} onChange={(e) => updateRow(i, "action", e.target.value)}
                    placeholder="Action taken" className="min-h-[34px] text-xs bg-white" data-testid={`${type}-action-${i}`} />
                </td>
                <td className="px-2 py-2 text-center">
                  <button type="button" onClick={() => removeRow(i)} className="text-[#EF4444] hover:text-[#DC2626] p-1" data-testid={`${type}-remove-${i}`}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className={`${color.bg} font-bold`}>
                <td className="px-2 py-2 text-center">Total</td>
                <td className="px-2 py-2 text-center font-mono">{totalQty.toFixed(2)}</td>
                <td colSpan="5"></td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
}

export { DEFECT_TYPES, REASON_6M };
